// API のエラーレスポンスを型付きで扱うための例外。ErrorPage が status を見て 4xx/5xx を出し分ける (Issue #3329)
export class ApiError extends Error {
  readonly status: number;
  // レスポンスの retry-after ヘッダ（秒）。429 のリトライ待機にのみ使う (Issue #3427)
  readonly retryAfterSeconds?: number;

  constructor(status: number, message: string, retryAfterSeconds?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

const DEFAULT_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 30000;
const MAX_RETRY_COUNT = 3;

function parseRetryAfterSeconds(response: Response): number | undefined {
  const header = response.headers.get('retry-after');
  // retry-after は HTTP-date 形式も許容されるため、数字のみのときだけ秒数として解釈する (Issue #3427)
  if (header === null || !/^\s*\d+\s*$/.test(header)) {
    return undefined;
  }

  const seconds = Number.parseInt(header, 10);
  return Number.isFinite(seconds) ? seconds : undefined;
}

// response.ok を見ずに body をそのまま返すと、401 の {success:false,error:...} が
// 正常データとして描画へ流れ込み、無関係な TypeError から 500 画面になる (Issue #3329)
export async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    // 422 の ValidationErrorResource は message が「入力エラーがあります」固定で、原因は errors 側にしか出ない
    const errors =
      typeof body === 'object' && body !== null ? (body as { errors?: unknown }).errors : undefined;
    const detail =
      Array.isArray(errors) && errors.length > 0 ? errors.map(String).join(' / ') : undefined;
    const message =
      detail ??
      (typeof body === 'object' && body !== null
        ? ((body as { error?: string; message?: string }).error ??
          (body as { message?: string }).message ??
          response.statusText)
        : response.statusText);
    throw new ApiError(
      response.status,
      message || response.statusText,
      parseRetryAfterSeconds(response)
    );
  }

  return response.json() as Promise<T>;
}

// retry-after を尊重するのは 429 だけ。5xx にプロキシが付けた分まで拾うと再試行が 1s/2s/4s から最大 30s 間隔に化ける (Issue #3427)
function throttledRetryDelayMs(error: unknown): number | undefined {
  if (!(error instanceof ApiError) || error.status !== 429) {
    return undefined;
  }
  return Math.max((error.retryAfterSeconds ?? 0) * 1000, DEFAULT_RETRY_DELAY_MS);
}

// 4xx は再試行しても結果が変わらないため retry しない。429 だけは retry-after 秒待てば回復するので例外扱いする (Issue #3427)
export function shouldRetryApiError(failureCount: number, error: unknown): boolean {
  const throttledDelay = throttledRetryDelayMs(error);
  if (throttledDelay !== undefined) {
    // Rack::Attack は固定窓のため指示より早く撃つと必ず 429。上限超の待機指示は再試行せずユーザー操作に委ねる (Issue #3427)
    return throttledDelay <= MAX_RETRY_DELAY_MS && failureCount < MAX_RETRY_COUNT;
  }
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
    return false;
  }
  return failureCount < MAX_RETRY_COUNT;
}

// 429 はサーバーが指定した retry-after を尊重する。それ以外は TanStack Query 既定と同じ指数バックオフ (Issue #3427)
export function apiErrorRetryDelay(failureCount: number, error: unknown): number {
  return (
    throttledRetryDelayMs(error) ??
    Math.min(DEFAULT_RETRY_DELAY_MS * 2 ** failureCount, MAX_RETRY_DELAY_MS)
  );
}
